import {
	faFilePdf as farFilePdf,
	faShieldCheck as farShieldCheck,
} from "@awesome.me/kit-3777c2145e/icons/classic/regular";
import {
	faCircleQuestion as fasCircleQuestion,
	faClockRotateLeft as fasClockRotateLeft,
	faInfo as fasInfo,
	faListUl as fasListUl,
} from "@awesome.me/kit-3777c2145e/icons/classic/solid";
import type { AbstractLeadFormProps } from "@component/assets/AbstractLeadForm/AbstractLeadForm";
import { AbstractResultItem } from "@component/assets/AbstractListingCalculator/AbstractResultItem";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ExpertBlockquote } from "@ui/components/ExpertBlockquote";
import { formatText } from "@ui/format";
import { faBankmonitor } from "@ui/fortawesome/bankmonitor";
import { getPromoText } from "@utils/product-marketing-text";
import { Button, InfoPopup, List, addParameterToURL } from "bm-ui-kit";
import lzString from "lz-string";
import type { ReactNode } from "react";
import { Alert, Table } from "react-bootstrap";
import { RetailAccountLeadForm } from "../LeadForm/RetailAccountLeadForm";
import { getLeadCalculatorRequest, getLeadCalculatorResponse } from "./domain";
import type { RetailAccountRequest, RetailAccountResponseItem, RetailAccountSearchParams } from "./types";

export class RetailAccountResultItem extends AbstractResultItem<
	RetailAccountSearchParams,
	RetailAccountRequest,
	RetailAccountResponseItem
> {
	protected renderProductData(): ReactNode {
		const { result } = this.props;
		const headerClass =
			"small font-weight-bold data-box text-center flex-sm-column justify-content-center align-items-center flex-row-reverse px-sm-2 px-2 py-2 data-header ";
		const resultClass =
			"small data-box text-center flex-sm-column justify-content-sm-center justify-content-between align-items-center flex-row-reverse py-lg-2 py-sm-2 py-1 px-sm-3 px-2 ";

		return (
			<div className="d-flex flex-wrap justify-content-between align-content-end w-100">
				<div className={`${headerClass}d-flex col-md-4`}>
					<span className="data-description d-block text-center">Össz. éves költség </span>
				</div>
				<InfoPopup
					className={`${headerClass}d-none d-md-flex col-md-2`}
					tooltip="Számlaveztési díj, Kedvezménycsomag díja, Számlanyitási díj, Postai kivonat díja, E-kivonat díja"
				>
					<span className="data-description d-block text-left text-sm-center">
						Alap díjak <FontAwesomeIcon icon={fasCircleQuestion} />
					</span>
				</InfoPopup>
				<InfoPopup
					className={`${headerClass}d-none d-md-flex col-md-2`}
					tooltip="Utalások, csoportos beszedések díja"
				>
					<span className="data-description d-block text-left text-sm-center">
						Tranzakciók <FontAwesomeIcon icon={fasCircleQuestion} />
					</span>
				</InfoPopup>
				<InfoPopup
					className={`${headerClass}d-none d-md-flex col-md-2`}
					tooltip="Kártyás vásárlás, ATM készpénzfelvétel költsége"
				>
					<span className="data-description d-block text-left text-sm-center">
						Kártyahasználat <FontAwesomeIcon icon={fasCircleQuestion} />
					</span>
				</InfoPopup>
				<InfoPopup
					className={`${headerClass}d-none d-md-flex col-md-2 last`}
					tooltip="SMS, Netbank havi díja, Bankkártya kibocsátási és rendszeres, éves költsége"
				>
					<span className="data-description d-block text-left text-sm-center">
						Egyéb <FontAwesomeIcon icon={fasCircleQuestion} />
					</span>
				</InfoPopup>

				<div className={`${resultClass}d-flex firstYear col-md-4 ordered-by`}>
					<strong className="data-value d-block text-center">
						{formatText(result.firstYear.annualTotalFee, "ft")}
					</strong>
				</div>
				<div className={`${resultClass}d-none d-md-flex col-md-2`}>
					<strong className="data-value d-block text-center">
						{formatText(result.firstYear.annualCost, "ft")}
					</strong>
				</div>
				<div className={`${resultClass}d-none d-md-flex col-md-2`}>
					<strong className="data-value d-block text-center">
						{formatText(result.firstYear.totalTransferCostAnnual, "ft")}
					</strong>
				</div>
				<div className={`${resultClass}d-none d-md-flex col-md-2`}>
					<strong className="data-value d-block text-center">
						{formatText(result.firstYear.cardUsageCostAnnual, "ft")}
					</strong>
				</div>
				<div className={`${resultClass}d-none d-md-flex col-md-2 last`}>
					<strong className="data-value d-block text-center">
						{formatText(result.firstYear.otherCostsAnnual, "ft")}
					</strong>
				</div>
				<div className={`${resultClass}d-flex secondYear col-md-4 ordered-by`}>
					<strong className="data-value d-block text-center">
						{formatText(result.secondYear.annualTotalFee, "ft")}
					</strong>
				</div>
				<div className={`${resultClass}d-none d-md-flex col-md-2`}>
					<strong className="data-value d-block text-center">
						{formatText(result.secondYear.annualCost, "ft")}
					</strong>
				</div>
				<div className={`${resultClass}d-none d-md-flex col-md-2`}>
					<strong className="data-value d-block text-center">
						{formatText(result.secondYear.totalTransferCostAnnual, "ft")}
					</strong>
				</div>
				<div className={`${resultClass}d-none d-md-flex col-md-2`}>
					<strong className="data-value d-block text-center">
						{formatText(result.secondYear.cardUsageCostAnnual, "ft")}
					</strong>
				</div>
				<div className={`${resultClass}d-none d-md-flex col-md-2 last`}>
					<strong className="data-value d-block text-center">
						{formatText(result.secondYear.otherCostsAnnual, "ft")}
					</strong>
				</div>
			</div>
		);
	}

	protected renderFeedbackLead(props: AbstractLeadFormProps): ReactNode {
		const { searchParams, result } = this.props;
		let title = "Kérj segítséget szakértőnktől!";

		if (this.isSpecialEco()) {
			title = "Ingyenes bankszámlát szeretnék!";
		}

		return (
			<RetailAccountLeadForm
				title={title}
				request={getLeadCalculatorRequest(searchParams)}
				response={getLeadCalculatorResponse(result)}
				{...props}
			/>
		);
	}

	protected renderFeedbackInterest(): ReactNode {
		if (this.isSpecialEco()) {
			return this.renderEcoInterest();
		}

		return (
			<>
				<div className="h4 miert-jobb">Miért jársz velünk jobban, mint a bankfiókban?</div>
				<List itemStyle="tick">
					<List.Item>
						<strong>Független szakértői csapat</strong> támogat a választásban
					</List.Item>
					<List.Item>
						<strong>Naprakész információkat, hasznos tippeket</strong> nyújtunk Neked
					</List.Item>
					<List.Item>
						<strong>Szolgáltatásunk díjmentes</strong>
					</List.Item>
					<List.Item>
						Kényelmes, <strong>gyors ügyintézés</strong> bankfióki sorban állás nélkül
					</List.Item>
				</List>
				<ExpertBlockquote
					expertId="sass"
					quote="Évente akár több tízezer forintot megtakaríthatsz az ideális bankszámla kiválasztásával. Segíthetek?"
					title="bankszámla szakértő"
				/>
			</>
		);
	}

	protected renderEcoInterest(): ReactNode {
		return (
			<div className="eco-info-block my-3 mx-sm-4">
				<div className="h1 text-uppercase">2 évig garantáltan ingyenes bankszámla</div>
				<List itemStyle="tick">
					<List.Item>
						<strong>0 Ft</strong> számlavezetési díj
					</List.Item>
					<List.Item>
						<strong>0 Ft</strong> tranzakciós illeték
					</List.Item>
					<List.Item>
						<strong>0 Ft</strong> átutalási díj
					</List.Item>
					<List.Item>
						<strong>0 Ft</strong> éves kártyadíj
					</List.Item>
				</List>
				<hr className="bg-white w-50 ml-0 my-2" />
				<div className="h3 pl-3 ml-3 mb-0">0 Ft összköltség</div>
				<div className="my-3">
					Bankmonitor Garancia!{" "}
					<strong>
						30 000 Ft-ot fizetünk ha <br className="d-none d-md-block" />
						összköltséged több mint 0 Ft!
					</strong>
				</div>
				<FontAwesomeIcon icon={farShieldCheck} className="mr-2 text-success" />
				<strong className="text-uppercase">BIZTONSÁGOS IGÉNYLÉS</strong>
				<List itemStyle="dash" size="sm">
					<List.Item>
						<strong>Titkosított rendszer</strong> – személyes adatvédelem
					</List.Item>
					<List.Item>
						<strong>E-nyomonkövetés,</strong> élőben a számlanyitás során
					</List.Item>
					<List.Item>
						<strong>Szakértői segítség,</strong> ha elakadnál az igénylésben
					</List.Item>
				</List>
				<FontAwesomeIcon icon={fasInfo} className="mr-2 text-success" />
				<strong className="text-uppercase">AMIRE SZÜKSÉGED LESZ:</strong>
				<List itemStyle="dash" size="sm">
					<List.Item>
						érvényes <strong>személyi igazolvány</strong> vagy <strong>kártya típusú</strong> vezetői
						engedély
					</List.Item>
					<List.Item>
						<strong>lakcímkártya</strong>
					</List.Item>
					<List.Item>
						<strong>kamerával rendelkező eszköz</strong> – laptop, mobil stb.
					</List.Item>
					<List.Item>
						<strong>személyes telefon</strong> – azonosításhoz
					</List.Item>
				</List>
			</div>
		);
	}

	protected renderFeedbackDetails(): ReactNode {
		const { result } = this.props;

		return (
			<>
				{this.renderDetailsPrepend()}
				<Table striped responsive="lg" size="sm">
					<thead>
						<tr>
							<th>Költség típusok</th>
							<th className="text-center" scope="col">
								Első évben
							</th>
							<th className="text-center" scope="col">
								Második évben
							</th>
						</tr>
					</thead>
					<tbody>
						<tr>
							<th scope="row">Alap díjak</th>
							<td className="text-center">{formatText(result.firstYear.annualCost, "ft")}</td>
							<td className="text-center">{formatText(result.secondYear.annualCost, "ft")}</td>
						</tr>
						<tr>
							<th scope="row">Tranzakciók éves díja</th>
							<td className="text-center">
								{formatText(result.firstYear.totalTransferCostAnnual, "ft")}
							</td>
							<td className="text-center">
								{formatText(result.secondYear.totalTransferCostAnnual, "ft")}
							</td>
						</tr>
						<tr>
							<th scope="row">Kártyahasználat éves díja</th>
							<td className="text-center">{formatText(result.firstYear.cardUsageCostAnnual, "ft")}</td>
							<td className="text-center">{formatText(result.secondYear.cardUsageCostAnnual, "ft")}</td>
						</tr>
						<tr>
							<th scope="row">SMS éves költségek</th>
							<td className="text-center">{formatText(result.firstYear.smsExpensesAnnual, "ft")}</td>
							<td className="text-center">{formatText(result.secondYear.smsExpensesAnnual, "ft")}</td>
						</tr>
						<tr>
							<th scope="row">Netbank éves díja</th>
							<td className="text-center">{formatText(result.firstYear.netbankCostAnnual, "ft")}</td>
							<td className="text-center">{formatText(result.secondYear.netbankCostAnnual, "ft")}</td>
						</tr>
						<tr>
							<th scope="row">Bankkártya éves díja</th>
							<td className="text-center">{formatText(result.firstYear.bankCardAnnualCost, "ft")}</td>
							<td className="text-center">{formatText(result.secondYear.bankCardAnnualCost, "ft")}</td>
						</tr>
					</tbody>
				</Table>

				<Table striped responsive="lg" size="sm">
					<thead>
						<tr>
							<th>Költségek összesen</th>
							<th className="text-center" scope="col">
								Első évben
							</th>
							<th className="text-center" scope="col">
								Második évben
							</th>
						</tr>
					</thead>
					<tbody>
						<tr>
							<th scope="row">Havonta</th>
							<td className="text-center">{formatText(result.firstYear.monthlyTotalFee, "ft")}</td>
							<td className="text-center">{formatText(result.secondYear.monthlyTotalFee, "ft")}</td>
						</tr>
						<tr>
							<th scope="row">Évente</th>
							<td className="text-center">{formatText(result.firstYear.annualTotalFee, "ft")}</td>
							<td className="text-center">{formatText(result.secondYear.annualTotalFee, "ft")}</td>
						</tr>
					</tbody>
				</Table>
				<div className="text-right mt-2">{this.renderDetailsBottomButtons()}</div>
			</>
		);
	}

	protected renderDetailsBottomButtons(): ReactNode {
		const { result } = this.props;

		const dataPageUrl = this.getFullDataPageUrl();

		let promoUrl: string;

		if (result.bank.code === "cib") {
			promoUrl = "https://www.cib.hu/document/documents/CIB/lak-promok/promo-bm_250401_final.pdf";
		}

		if (result.name.includes("Partner Aktív Nulla") && result.bank.code === "unicredit") {
			promoUrl =
				"https://www.unicreditbank.hu/content/dam/cee2020-pws-hu/Rolunk/Kondcios_listak/Maganszemelyek/202505/Hirdetmeny_es_Reszveteli_Feltetelek_20250506.pdf";
		}

		return (
			<>
				{!!promoUrl && (
					<>
						<Button
							className={`float-sm-left mb-2 mb-sm-0${
								result.bank.code === "cib" && " eco-promo-rules-button"
							}`}
							variant="dark"
							href={promoUrl}
							target="_blank"
							icon={<FontAwesomeIcon icon={farFilePdf} />}
							outlined
						>
							Promóciós szabályzat
						</Button>
						<br className="d-sm-none" />
					</>
				)}
				<Button
					variant={promoUrl ? "dark" : "light"}
					rel="nofollow"
					href={dataPageUrl}
					target="_blank"
					icon={<FontAwesomeIcon icon={fasListUl} />}
					className={result.bank.code === "cib" ? "eco-datapage-button" : ""}
					onClick={this.onProductPageClick}
				>
					Termék adatlap
				</Button>
			</>
		);
	}

	protected renderDetailsPrepend(): ReactNode {
		if (this.isSpecialEco()) {
			return (
				<div className="eco-info-block my-3 mx-sm-4">
					<div className="h1 text-uppercase">2 évig garantáltan ingyenes bankszámla</div>
					<div className="my-3">
						Bankmonitor Garancia!{" "}
						<strong>
							30 000 Ft-ot fizetünk ha <br className="d-none d-md-block" />
							összköltséged több mint 0 Ft!
						</strong>
					</div>
				</div>
			);
		}

		return (
			<Alert variant="info">
				A bankszámla kalkulátorban beállított számlahasználat, jövedelem és egyéb információk felhasználásával
				kiszámított értékek. A kalkulátor automatikusan számol a felhasználó által megadott értékek alapján az
				igénybe vehető kedvezményekkel.
				<br />
				<strong>
					Internetes felületen vagy mobilbankon keresztül leadott átutalási megbízás néhány bank esetében
					eltérő díjtételt jelenthet.
				</strong>
			</Alert>
		);
	}

	protected renderBmPromo(): ReactNode {
		const { result } = this.props;

		if (result.marketing?.cssRule) {
			const promoTexts = getPromoText(result.marketing.cssRule);
			if (promoTexts?.listingDesktopText || promoTexts?.listingMobileText) {
				return (
					<>
						{this.isSpecialEco() && this.renderGuaranteedFree()}
						<div className="product-result-promo">
							{promoTexts.listingIcon && (
								<FontAwesomeIcon icon={promoTexts.listingIcon} className="mr-1" />
							)}
							<span className="d-none d-sm-inline">
								{promoTexts.listingDesktopText || promoTexts.listingMobileText}
							</span>
							{!!promoTexts.listingMobileText && (
								<span className="d-sm-none">{promoTexts.listingMobileText}</span>
							)}
						</div>
					</>
				);
			}
		}

		if (this.isSpecialEco()) {
			return (
				<>
					{this.renderGuaranteedFree()}
					<div className="product-result-promo">
						<FontAwesomeIcon icon={fasClockRotateLeft} className="mr-1" />
						<span className="d-none d-sm-inline">
							<strong>ONLINE</strong> bankszámla nyitás,{" "}
						</span>
						<span className="d-sm-none">
							<strong>ONLINE</strong>,{" "}
						</span>
						0-24 órában köthető
					</div>
				</>
			);
		}

		if (this.isErstePrivi() || this.isUnicreditZero()) {
			return (
				<div className="product-result-promo">
					<FontAwesomeIcon icon={fasClockRotateLeft} className="mr-1" />
					<span className="d-none d-sm-inline">
						<strong>ONLINE</strong> bankszámla nyitás,{" "}
					</span>

					<strong>{this.isErstePrivi() ? "40 000" : "30 000"} Ft jóváírással</strong>
				</div>
			);
		}

		return super.renderBmPromo();
	}

	protected renderGuaranteedFree(): ReactNode {
		return (
			<div className="bm-only-badge">
				<FontAwesomeIcon icon={faBankmonitor} className="mr-1" />
				<strong>Garantáltan ingyenes</strong> bankszámla!
			</div>
		);
	}

	protected getDataPageUrl(): string {
		const { result, searchParams } = this.props;

		return addParameterToURL(`/bankszamla/adatlap/`, {
			ID: result.id,
			d: lzString.compressToEncodedURIComponent(JSON.stringify(searchParams)),
			tab: "RETAIL_ACCOUNT",
		});
	}

	private isSpecialEco(): boolean {
		const { result } = this.props;

		return result?.marketing?.cssRule?.includes("cib-eco-badge");
	}

	private isUnicreditZero(): boolean {
		const { result } = this.props;

		return result?.name?.includes("Partner Aktív") && !result.marketing.newTargetUrl;
	}

	private isErstePrivi(): boolean {
		const { result } = this.props;

		return result?.name?.includes("Erste Privilégium") && !result.marketing.newTargetUrl;
	}

	protected getComponentName(): string {
		return "RetailAccountResultItem";
	}
}
